// src/pages/base/Base.hooks.js

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { squadAPI, researchAPI, facilityAPI, campaignAPI } from '../../api';
import { loadCampaign, getCurrentUser } from '../../utils/loadCampaign';
import { saveCompleteGameState } from '../../utils/saveCampaign';
import { advanceDay } from '../../utils/gameengine'; // Use the pure game engine
import { processMissionCasualties, applyThemeToEvents } from './Base.utils';
import { DAY_PROGRESS_INCREMENT, HEAL_CONFIG } from './Base.constants';
import { advanceDayWithMissions } from '../../componets/missioncontrol'; // ✅ Import from barrel export

/**
 * Hook to manage campaign initialization
 */
export const useCampaignInitialization = (location, setters) => {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeCampaign = async () => {
      try {
        setLoading(true);
        const campaignState = await loadCampaign(location.state, currentUser?.id);
        
        if (!campaignState) {
          navigate('/');
          return;
        }

        setters.setCurrentCampaign(campaignState.campaign);
        setters.setResources(campaignState.resources);
        setters.setFacilities(campaignState.facilities);
        setters.setResearch(campaignState.research);
        setters.setCurrentResearch(campaignState.activeResearch);

        // Process casualties if returning from mission
        if (location.state?.casualties) {
          const updatedSquad = processMissionCasualties(
            campaignState.squad,
            location.state.casualties
          );
          setters.setSquad(updatedSquad);
          
          // Persist to backend
          for (const soldier of updatedSquad) {
            await squadAPI.updateSquadMember(soldier.id, {
              status: soldier.status,
              health: soldier.health,
              missions_completed: soldier.missionsCompleted,
              experience: soldier.experience
            });
          }
        } else {
          setters.setSquad(campaignState.squad);
        }
      } catch (error) {
        console.error('Failed to initialize campaign:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    initializeCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, currentUser?.id, navigate]); // setters is intentionally omitted - it's stable from parent

  return { loading };
};

/**
 * Hook to manage day advancement and timer
 * 
 * This uses the NEW pure game engine approach:
 * 1. advanceDay() is pure - no API calls, just calculations
 * 2. We update campaign day in the database separately
 * 3. We save all game state after updating React state
 * 4. Apply theme to raw events for UI display
 * 5. Progress active missions and resolve completed ones
 * 
 * @param {Object} currentCampaign - Current campaign state
 * @param {Object} gameState - Current game state (squad, facilities, research, resources, activeMissions)
 * @param {Object} setters - State setter functions
 * @param {Object} themeBundle - Theme bundle containing event library for applying narrative content
 */
export const useDayAdvancement = (currentCampaign, gameState, setters, themeBundle) => {
  const [isAdvancingDay, setIsAdvancingDay] = useState(false);
  const [dayProgress, setDayProgress] = useState(0);

  const handleAdvanceDay = useCallback(async () => {
    if (isAdvancingDay || !currentCampaign?.id) return;

    try {
      setIsAdvancingDay(true);

      const newDay = (currentCampaign.day || 1) + 1;

      // Step 1: Update campaign day in database first
      await campaignAPI.updateCampaign(currentCampaign.id, { day: newDay });

      // Step 2: Progress missions FIRST (before other game systems)
      const missionResults = advanceDayWithMissions({
        activeMissions: gameState.activeMissions || [],
        squad: gameState.squad,
        resources: gameState.resources,
      });

      // Step 3: Run pure game engine calculations with updated squad/resources
      const updatedState = advanceDay(
        currentCampaign,
        {
          squad: missionResults.squad,
          facilities: gameState.facilities,
          currentResearch: gameState.currentResearch,
          researchList: gameState.research,
          resources: missionResults.resources,
          activeMissions: missionResults.activeMissions,
          campaign: currentCampaign
        },
        themeBundle.events
      );

      // Step 4: Update React state with new values
      const newCampaignState = { ...currentCampaign, day: newDay };
      
      setters.setCurrentCampaign(newCampaignState);
      setters.setSquad(updatedState.squad);
      setters.setFacilities(updatedState.facilities);
      setters.setResources(updatedState.resources);
      
      // Update active missions
      if (setters.setActiveMissions) {
        setters.setActiveMissions(missionResults.activeMissions);
      }

      // Handle research completion
      if (updatedState.research.completed) {
        setters.setCurrentResearch(null);
      } else if (updatedState.research.currentResearch) {
        setters.setCurrentResearch(updatedState.research.currentResearch);
      }

      setters.setResearch(updatedState.research.researchList);

      // Step 5: Apply theme to raw events before setting state
      const themedEvents = applyThemeToEvents(
        updatedState.events,
        themeBundle.events
      );

      // Step 6: Add mission completion events to day events
      const missionEvents = missionResults.completedMissions.map(mission => ({
        type: mission.success ? 'mission_success' : 'mission_failed',
        title: mission.success ? 'Mission Success' : 'Mission Failed',
        description: `${mission.name} has been ${mission.success ? 'completed successfully' : 'failed'}`,
        mission: mission,
        icon: mission.success ? '✓' : '✗',
        color: mission.success ? 'green' : 'red',
      }));

      setters.setDayEvents([...themedEvents, ...missionEvents]);

      // Step 7: Save complete game state
      await saveCompleteGameState({
        campaign: newCampaignState,
        resources: updatedState.resources,
        facilities: updatedState.facilities,
        squad: updatedState.squad,
        research: updatedState.research.researchList,
        currentResearch: updatedState.research.currentResearch
      });

      console.log('✅ Day advanced and saved:', newDay);
      
      // Log mission results
      if (missionResults.completedMissions.length > 0) {
        console.log('🎯 Missions completed:', missionResults.completedMissions.map(m => ({
          name: m.name,
          success: m.success,
          casualties: m.casualties?.length || 0,
        })));
      }

      // Show summary if needed
      if (themedEvents.length > 0 || updatedState.research.completed || missionEvents.length > 0) {
        setters.setShowDaySummary(true);
      }

    } catch (error) {
      console.error('❌ Failed to advance day:', error);
    } finally {
      setIsAdvancingDay(false);
      setDayProgress(0);
    }
  }, [isAdvancingDay, currentCampaign, gameState, setters, themeBundle]);

  // Auto-advance day timer
  useEffect(() => {
    if (!currentCampaign?.id || isAdvancingDay) return;
    
    const timerInterval = setInterval(() => {
      setDayProgress(prev => {
        const newProgress = prev + DAY_PROGRESS_INCREMENT;
        
        if (newProgress >= 100) {
          // Trigger day advancement
          handleAdvanceDay();
          return 0;
        }
        
        return newProgress;
      });
    }, 1000);
    
    return () => clearInterval(timerInterval);
  }, [currentCampaign?.id, isAdvancingDay, handleAdvanceDay]);

  return { handleAdvanceDay, isAdvancingDay, dayProgress };
};

/**
 * Hook to provide all API handlers
 */
export const useBaseAPI = (currentCampaign, setters) => {
  const apiHandlers = {
    // Squad operations
    createSquadMember: async (name, rank, stats) => {
      const newMember = await squadAPI.createSquadMember(
        currentCampaign.id,
        name,
        rank,
        stats
      );
      setters.setSquad(prev => [...prev, newMember]);
      return newMember;
    },

    updateSquadMember: async (memberId, updates) => {
      await squadAPI.updateSquadMember(memberId, updates);
      setters.setSquad(prev => prev.map(s => 
        s.id === memberId ? { ...s, ...updates } : s
      ));
    },

    healSquadMember: async (soldierId, amount) => {
      await squadAPI.healSquadMember(soldierId, amount);
      setters.setSquad(prev => prev.map(s => 
        s.id === soldierId 
          ? { ...s, health: Math.min(s.health + amount, s.max_health) }
          : s
      ));
    },

    // Research operations
    createResearch: async (type, name, progress) => {
      const newResearch = await researchAPI.createResearch(
        currentCampaign.id,
        type,
        name,
        progress
      );
      setters.setResearch(prev => [...prev, newResearch]);
      return newResearch;
    },

    startResearch: async (campaignId, project) => {
      if (!campaignId || !project) {
        throw new Error('Campaign ID and project are required');
      }

      const createdResearch = await researchAPI.createResearch(
        campaignId,
        project.id,
        project.name,
        0
      );

      setters.setResearch(prev => [...prev, createdResearch]);
      return createdResearch;
    },

    // Facility operations
    createFacility: async (type, name, row, col, level) => {
      const newFacility = await facilityAPI.createFacility(
        currentCampaign.id,
        type,
        name,
        row,
        col,
        level
      );
      setters.setFacilities(prev => [...prev, { 
        ...newFacility, 
        is_active: true,
        isDirty: true
      }]);
      return newFacility;
    },

    upgradeFacility: async (facilityId, newLevel) => {
      await facilityAPI.upgradeFacility(facilityId, newLevel);
      setters.setFacilities(prev => prev.map(f => 
        f.id === facilityId ? { ...f, level: newLevel } : f
      ));
    }
  };

  return apiHandlers;
};

/**
 * Hook to handle soldier healing
 */
export const useHealSoldier = (apiHandlers, setResources) => {
  const handleHealSoldier = async (soldier) => {
    setResources(prev => ({
      ...prev,
      supplies: prev.supplies - HEAL_CONFIG.COST.supplies,
      funds: prev.funds - HEAL_CONFIG.COST.funds
    }));

    try {
      await apiHandlers.healSquadMember(soldier.id, HEAL_CONFIG.AMOUNT);
    } catch (error) {
      console.error('Failed to heal soldier:', error);
      setResources(prev => ({
        ...prev,
        supplies: prev.supplies + HEAL_CONFIG.COST.supplies,
        funds: prev.funds + HEAL_CONFIG.COST.funds
      }));
    }
  };

  return handleHealSoldier;
};